/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Injectable } from '@angular/core';


import { Url_Images, Url_ImageById } from 'app/rest-constants';
import { BaseInstance } from 'app/models/base-instance';
import { Image } from 'app/models/image';
import { User } from 'app/models/user';
import { RestService } from 'app/services/rest-service';
import { AntennaInstance } from 'app/models/antenna-instance';
import { ReaderInstance } from 'app/models/reader-instance';
import { ObjectInstance } from 'app/models/object-instance';
import { BaseEntity } from 'app/models/base-entity';
import { TagInstance } from 'app/models/tag-instance';
import { DEFAULT_ANTENNA_ICON, DEFAULT_NO_IMAGE, DEFAULT_READER_ICON, DEFAULT_TRACKING_ICON, DEFAULT_USER_IMAGE } from 'app/app-constants';

@Injectable()
export class ImageService {

  constructor(
    private mRestService: RestService
  ) { }

  
  getAllImages(): Promise<Image[]> {
    return this.mRestService.get<Image[]>(Url_Images);
  }

  getImagesFiltered(start: number, size: number, order: string, filterString: string): Promise<Image[]> {
    const url = Url_Images + `filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<Image[]>(url);
  } 

  public uploadImageFile(imageFile: File): Promise<Image> {
    //Build the Form Data containing the Image File to upload 
    const formData = new FormData();
    formData.append("file", imageFile);
    return this._uploadImageFile(formData);
  }

  public uploadImage(formData: FormData): Promise<Image> {
    return this._uploadImageFile(formData);
  }

  private _uploadImageFile(formData: FormData): Promise<Image> {
    return this.mRestService.upload<Image>(Url_Images, formData);
  }





  /**
   * Returns the "src" value for an "img" HTML Element to use for the Image for the given Entity.
   * - Used by:
   *   1) Entity Type Dialog Component
   */
  public getImageSourceForEntity(entity: BaseEntity): string {
    if (entity != null && entity.image != null && entity.image.id != null) {
      return this.getImageSourceForImageId(entity.image.id);
    }
    return DEFAULT_NO_IMAGE;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Image for the given Instance.
   * - Used by:
   *   1) Entity Instance Dialog Component
   */
  public getImageSourceForInstance(instance: BaseInstance): string {
    if (instance != null && instance.image != null && instance.image.id != null) {
      return this.getImageSourceForImageId(instance.image.id);
    }
    return DEFAULT_NO_IMAGE;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Image for the given Object Instance.
   * - Used by:
   *   1) Asset Popup Dialog Component
   *   2) People Popup Dialog Component
   *   3) Locate Component
   *   4) Main People Component
   */
  public getImageSourceForObjectInstance(objectInstance: ObjectInstance): string {
    if (objectInstance != null) {
      if (objectInstance.image != null && objectInstance.image.id != null) {
        return this.getImageSourceForImageId(objectInstance.image.id);
      } else if (objectInstance.object != null && objectInstance.object.image != null && objectInstance.object.image.id != null) {
        return this.getImageSourceForImageId(objectInstance.object.image.id);
      }
    }
    return DEFAULT_NO_IMAGE;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Image for the given Tag.
   * - Used by:
   *   1) Monitor Tags Component
   */
  public getImageSourceForTag(tag: TagInstance): string {
    if (tag != null) {
      if (tag.image != null && tag.image.id != null) {
        return this.getImageSourceForImageId(tag.image.id);
      } else if (tag.asset != null && tag.asset.image != null && tag.asset.image.id != null) {
        return this.getImageSourceForImageId(tag.asset.image.id);
      }
    }
    return DEFAULT_NO_IMAGE;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Image for the given User.
   * - Used by:
   *   1) User Dialog Component
   *   2) Users Component
   *   3) Header Component's Profile Image (Redirected here, was defaulting to a different Image: "assets/images/genericPerson.png")
   */
  public getImageSourceForUser(user: User): string {
    if (user != null && user.image != null && user.image.id != null) {
      return this.getImageSourceForImageId(user.image.id);
    }
    return DEFAULT_USER_IMAGE;
  }




  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Entity.
   * - Used by:
   *   1) Entity Type Dialog Component
   */
  public getIconSourceForEntity(entity: BaseEntity): string {
    if (entity != null && entity.icon != null && entity.icon.id != null) {
      return this.getImageSourceForImageId(entity.icon.id);
    }
    return DEFAULT_NO_IMAGE;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Instance.
   * - Used by:
   *   1) Entity Instance Dialog Component
   */
  public getIconSourceForInstance(instance: BaseInstance): string {
    if (instance != null && instance.icon != null && instance.icon.id != null) {
      return this.getImageSourceForImageId(instance.icon.id);
    }
    return DEFAULT_NO_IMAGE;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Antenna Instance.
   * - Used by:
   *   1) Sites Component
   */
  public getIconSourceForAntennaInstance(antennaInstance: AntennaInstance): string {
    if (antennaInstance != null) {
      if (antennaInstance.icon != null && antennaInstance.icon.id != null) {
        return this.getImageSourceForImageId(antennaInstance.icon.id);
      } else if (antennaInstance.antenna != null && antennaInstance.antenna.icon != null && antennaInstance.antenna.icon.id != null) {
        return this.getImageSourceForImageId(antennaInstance.antenna.icon.id);
      }
    }
    return DEFAULT_ANTENNA_ICON;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Reader Instance.
   * - Used by:
   *   1) Sites Component
   */
  public getIconSourceForReaderInstance(readerInstance: ReaderInstance): string {
    if (readerInstance != null) {
      if (readerInstance.icon != null && readerInstance.icon.id != null) {
        return this.getImageSourceForImageId(readerInstance.icon.id);
      } else if (readerInstance.reader != null && readerInstance.reader.icon != null && readerInstance.reader.icon.id != null) {
        return this.getImageSourceForImageId(readerInstance.reader.icon.id);
      }
    }
    return DEFAULT_READER_ICON;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Object Instance.
   * - Used by:
   *   1) Sites Component
   *   2) Map Component
   *   3) Asset Popup Dialog Component
   *   4) People Popup Dialog Component
   *   5) Locate Component
   */
  public getIconSourceForObjectInstance(objectInstance: ObjectInstance): string {
    if (objectInstance != null) {
      if (objectInstance.icon != null && objectInstance.icon.id != null) {
        return this.getImageSourceForImageId(objectInstance.icon.id);
      } else if (objectInstance.object != null && objectInstance.object.icon != null && objectInstance.object.icon.id != null) {
        return this.getImageSourceForImageId(objectInstance.object.icon.id);
      }
    }
    return DEFAULT_TRACKING_ICON;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Tag.
   * - Used by:
   *   1) Map Component
   *   2) Monitor Tags Component
   */
  public getIconSourceForTag(tag: TagInstance): string {
    if (tag != null) {
      if (tag.icon != null && tag.icon.id != null) {
        return this.getImageSourceForImageId(tag.icon.id);
      } else if (tag.asset != null && tag.asset.icon != null && tag.asset.icon.id != null) {
        return this.getImageSourceForImageId(tag.asset.icon.id);
      }
    }
    return DEFAULT_TRACKING_ICON;
  }

  /**
   * Returns the "src" value for an "img" HTML Element to use for the Icon for the given Image ID.
   * - Used by:
   *   1) Sites Component (Location Image)
   *   2) Map Component
   */
  public getImageSourceForImageId(imageId: number): string {
    return Url_ImageById(imageId);
  }

}
